import { useState, useEffect } from "react";
import {
  Box,
  Container,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Stack,
  Typography,
  CircularProgress,
  Alert,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ReadMoreIcon from "@mui/icons-material/ReadMore";
import useApi from "../../hooks/useApi";
import PageHeader from "../layout/PageHeader";
import MarkdownViewer from "../documentation/MarkdownViewer";

const DOCUMENTATION_TYPES = [
  "user",
  "technical",
  "architecture",
  "troubleshooting",
];

const DocumentationCenter = () => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [allTags, setAllTags] = useState([]);

  // View state
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [openViewer, setOpenViewer] = useState(false);

  const { postData } = useApi();

  // Load all documentation on mount
  useEffect(() => {
    loadDocumentation();
  }, []);

  // Extract unique tags from docs
  useEffect(() => {
    const tags = new Set();
    docs.forEach((doc) => {
      if (doc.tags && Array.isArray(doc.tags)) {
        doc.tags.forEach((tag) => tags.add(tag));
      }
    });
    setAllTags(Array.from(tags).sort());
  }, [docs]);

  const loadDocumentation = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await postData({
        url: "/api/entity",
        entityName: "Documentation",
        action: "list",
        data: {},
      });

      if (response && response.data) {
        setDocs(Array.isArray(response.data) ? response.data : []);
      }
    } catch (err) {
      console.error("Error loading documentation:", err);
      setError("Failed to load documentation");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);

    if (!query.trim()) {
      loadDocumentation();
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await postData({
        url: "/api/entity",
        entityName: "Documentation",
        action: "search",
        data: {
          query,
          type: selectedType || undefined,
        },
      });

      if (response && response.data) {
        setDocs(Array.isArray(response.data) ? response.data : []);
      }
    } catch (err) {
      console.error("Error searching documentation:", err);
      setError("Failed to search documentation");
    } finally {
      setLoading(false);
    }
  };

  // Filter displayed docs
  const filteredDocs = docs.filter((doc) => {
    // Type filter
    if (selectedType && doc.type !== selectedType) {
      return false;
    }

    // Tags filter
    if (selectedTags.length > 0) {
      const hasAllTags = selectedTags.every((tag) => doc.tags?.includes(tag));
      if (!hasAllTags) {
        return false;
      }
    }

    return true;
  });

  const handleViewDoc = async (doc) => {
    try {
      setLoading(true);
      // Fetch full document with content
      const response = await postData({
        url: "/api/entity",
        entityName: "Documentation",
        action: "getById",
        data: { id: doc.id || doc._id },
      });

      if (response && response.data) {
        setSelectedDoc(response.data);
        setOpenViewer(true);
      }
    } catch (err) {
      console.error("Error loading document:", err);
      setError("Failed to load document");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHeader pageName="documentation-center" />

      <Container
        maxWidth="lg"
        sx={{ py: 3 }}
      >
        <Stack spacing={3}>
          {/* Header */}
          <Box>
            <Typography
              variant="h4"
              sx={{ fontWeight: "bold", mb: 1 }}
            >
              Documentation Center
            </Typography>
            <Typography
              variant="body1"
              color="textSecondary"
            >
              Discover guides, API references, architecture docs, and
              troubleshooting articles
            </Typography>
          </Box>

          {/* Search and Filters */}
          <Paper sx={{ p: 2 }}>
            <Stack spacing={2}>
              {/* Search */}
              <TextField
                fullWidth
                placeholder="Search documentation..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />

              {/* Filters */}
              <Stack
                direction="row"
                spacing={2}
              >
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={selectedType}
                    label="Type"
                    onChange={(e) => setSelectedType(e.target.value)}
                  >
                    <MenuItem value="">All Types</MenuItem>
                    {DOCUMENTATION_TYPES.map((type) => (
                      <MenuItem
                        key={type}
                        value={type}
                      >
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>Tags</InputLabel>
                  <Select
                    multiple
                    value={selectedTags}
                    label="Tags"
                    onChange={(e) => setSelectedTags(e.target.value)}
                  >
                    {allTags.map((tag) => (
                      <MenuItem
                        key={tag}
                        value={tag}
                      >
                        {tag}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {(selectedType || selectedTags.length > 0) && (
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setSelectedType("");
                      setSelectedTags([]);
                      loadDocumentation();
                    }}
                  >
                    Reset Filters
                  </Button>
                )}
              </Stack>
            </Stack>
          </Paper>

          {/* Error Alert */}
          {error && (
            <Alert
              severity="error"
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {/* Results Count */}
          <Typography
            variant="subtitle2"
            color="textSecondary"
          >
            Found {filteredDocs.length} documentation
            {filteredDocs.length !== 1 ? "s" : ""}
          </Typography>

          {/* Loading */}
          {loading && <CircularProgress sx={{ mx: "auto" }} />}

          {/* Documentation Grid */}
          {!loading && (
            <Grid
              container
              spacing={2}
            >
              {filteredDocs.length === 0 ? (
                <Grid
                  item
                  xs={12}
                >
                  <Paper sx={{ p: 4, textAlign: "center" }}>
                    <Typography color="textSecondary">
                      {searchQuery || selectedType || selectedTags.length > 0
                        ? "No documentation matches your search"
                        : "No documentation available"}
                    </Typography>
                  </Paper>
                </Grid>
              ) : (
                filteredDocs.map((doc) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    key={doc.id || doc.key}
                  >
                    <Card
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        "&:hover": {
                          boxShadow: 4,
                          transform: "translateY(-4px)",
                        },
                      }}
                      onClick={() => handleViewDoc(doc)}
                    >
                      <CardContent sx={{ flex: 1 }}>
                        {/* Type Badge */}
                        <Box sx={{ mb: 1 }}>
                          <Chip
                            label={doc.type}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Box>

                        {/* Title */}
                        <Typography
                          variant="h6"
                          component="div"
                          sx={{
                            mb: 1,
                            fontWeight: "bold",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {doc.title}
                        </Typography>

                        {/* Description */}
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{
                            mb: 2,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {doc.description || "No description available"}
                        </Typography>

                        {/* Metadata */}
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ mb: 2 }}
                        >
                          {doc.readingTimeMinutes && (
                            <Typography
                              variant="caption"
                              color="textSecondary"
                            >
                              📖 {doc.readingTimeMinutes} min read
                            </Typography>
                          )}
                          {doc.creatorName && (
                            <Typography
                              variant="caption"
                              color="textSecondary"
                            >
                              👤 {doc.creatorName}
                            </Typography>
                          )}
                        </Stack>

                        {/* Tags */}
                        {doc.tags && doc.tags.length > 0 && (
                          <Stack
                            direction="row"
                            spacing={0.5}
                            sx={{ flexWrap: "wrap" }}
                          >
                            {doc.tags.slice(0, 3).map((tag) => (
                              <Chip
                                key={tag}
                                label={tag}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                            {doc.tags.length > 3 && (
                              <Typography
                                variant="caption"
                                sx={{ alignSelf: "center" }}
                              >
                                +{doc.tags.length - 3} more
                              </Typography>
                            )}
                          </Stack>
                        )}
                      </CardContent>

                      {/* Actions */}
                      <CardActions>
                        <Button
                          size="small"
                          startIcon={<ReadMoreIcon />}
                          fullWidth
                        >
                          Read More
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))
              )}
            </Grid>
          )}
        </Stack>
      </Container>

      {/* Documentation Viewer Modal */}
      {selectedDoc && openViewer && (
        <MarkdownViewer
          open={openViewer}
          onClose={() => {
            setOpenViewer(false);
            setSelectedDoc(null);
          }}
          docs={[selectedDoc]}
          selectedDoc={selectedDoc}
          onDocSelect={setSelectedDoc}
        />
      )}
    </>
  );
};

export default DocumentationCenter;
